import { useEffect, useContext, useState } from "react";
import { DataContext } from "../dataContext";
import useApi from "../api/api";
import { applyUi } from "./ui";
import { applyTabBranding } from "./tabBranding";

const FiBranding = () => {
  const api = useApi(false);
  const { loggedIn, setLoggedIn } = useContext(DataContext);

  const defaultBranding = {
    sending_colors: {
      sending_bg_primary: "#ffffff",
      sending_bg_secondary: "#1d74ec",
      sending_bg_tertiary: "#edf3fc",
      sending_text_primary: "#282f38",
      sending_text_secondary: "#758395",
      sending_alert_color: "#ff4620",
      sending_success_color: "#13a61f",
    },
    sms_customization: {
      off_net_first: "I am sending you",
      off_net_second: "from my account at",
      off_net_third: "with the note",
      off_net_fourth: "Click this link to receive your funds:",
      on_net_first: "I am sending you",
      on_net_second: "with the note",
      on_net_third:
        "The funds will be available shortly, and visible via online banking.",
    },
    send_on_network_notification: false,
    event_demo: false,
    show_confirmation_page: false,
    // Visual refresh feature flag (fi_branding.enable_sending_ui_refresh).
    // Off by default: the pre-refresh UI renders until the FI is opted in.
    enable_sending_ui_refresh: false,
    // Card entry iframe refresh flag (fi_branding.enable_cde_ui_refresh), passed
    // through to the CDE funding iframe as the `ui_refresh` query param.
    enable_cde_ui_refresh: false,
    // Browser tab favicon and title (fi_branding.app_favicon_url /
    // app_tab_title). Empty keeps the defaults from public/index.html.
    app_favicon_url: "",
    app_tab_title: "",
  };

  const [branding, setBranding] = useState(defaultBranding);
  // `loaded`: this FI's branding is in hand (or failed while logged in, so the
  // defaults stand). Until then the defaults are placeholders and must not pick
  // the UI. `checked`: the first request has settled either way, so a
  // logged-out screen can tell "not signed in" from "still finding out".
  const [loaded, setLoaded] = useState(false);
  const [checked, setChecked] = useState(false);
  const root = document.querySelector(":root");

  useEffect(() => {
    const getFiBranding = async () => {
      try {
        const response = await api.get("ui/v2");
        setBranding(response.data);
        setLoaded(true);
        // TODO this goes with the question below, setLoggedIn true if successful ui call
        setLoggedIn(true);
      } catch (error) {
        console.error("get branding error", error);
        // Signed in but no branding: render with the defaults, as before.
        if (loggedIn) {
          setLoaded(true);
        }
      } finally {
        setChecked(true);
      }
    };
    if (loggedIn) {
      getFiBranding();
    } else {
      // TODO do we want to do something like this? attempt to hit a benign endpoint
      // to check and see if we're authenticated? Could be useful if the user refreshes
      // the page, "loggedIn" will be false but they will still be authenticated
      setTimeout(() => {
        getFiBranding();
      }, 500);
    }
  }, [api, loggedIn, setLoggedIn]);

  // Tab favicon and title, once this FI's branding is in hand.
  const faviconUrl = branding?.app_favicon_url;
  const tabTitle = branding?.app_tab_title;
  useEffect(() => {
    if (loaded) {
      applyTabBranding({ faviconUrl, title: tabTitle });
    }
  }, [loaded, faviconUrl, tabTitle]);

  // Select the UI generation (and therefore the scoped stylesheet). Only an
  // explicit `true` opts an FI in.
  const uiRefresh = branding?.enable_sending_ui_refresh === true;
  // Before branding loads, keep what applyCachedUi set so a refresh FI doesn't
  // flash legacy (and so the cache isn't overwritten with "legacy" every load).
  if (loaded) {
    applyUi(uiRefresh);
  }

  const isColor = (str) => {
    if (/^#[0-9A-F]{6}$/i.test(str) === true) {
      return true;
    }
  };

  // check if all branding colors are valid hex codes and set css variables if so
  if (
    branding &&
    isColor(branding?.sending_colors?.sending_bg_primary) &&
    isColor(branding?.sending_colors?.sending_bg_secondary) &&
    isColor(branding?.sending_colors?.sending_bg_tertiary) &&
    isColor(branding?.sending_colors?.sending_text_primary) &&
    isColor(branding?.sending_colors?.sending_text_secondary) &&
    isColor(branding?.sending_colors?.sending_alert_color) &&
    isColor(branding?.sending_colors?.sending_success_color)
  ) {
    root.style.setProperty(
      "--sending_bg_primary",
      branding.sending_colors.sending_bg_primary
    );
    root.style.setProperty(
      "--sending_bg_secondary",
      branding.sending_colors.sending_bg_secondary
    );
    root.style.setProperty(
      "--sending_bg_tertiary",
      branding.sending_colors.sending_bg_tertiary
    );
    root.style.setProperty(
      "--sending_text_primary",
      branding.sending_colors.sending_text_primary
    );
    root.style.setProperty(
      "--sending_text_secondary",
      branding.sending_colors.sending_text_secondary
    );
    root.style.setProperty(
      "--sending_alert_color",
      branding.sending_colors.sending_alert_color
    );
    root.style.setProperty(
      "--sending_success_color",
      branding.sending_colors.sending_success_color
    );
    // Refresh: the FI's brand colour is reserved for the primary action,
    // badges and accent marks. Every accent surface reads --fi-accent; the
    // pressed shade is derived with relative colour syntax (Safari 16.4+ /
    // Chrome 119+).
    root.style.setProperty(
      "--fi-accent",
      branding.sending_colors.sending_bg_secondary
    );
    root.style.setProperty(
      "--fi-accent-dark",
      `oklch(from ${branding.sending_colors.sending_bg_secondary} calc(l * 0.86) c h)`
    );
  }

  // react-select styling for the refreshed look. The control sits inside a
  // grouped-list row (SendMoney) or on the page (IncomingDetails, scheduled
  // flows), so it is borderless and transparent; the menu is a white card.
  const refreshDropDownStyle = {
    control: (base, state) => ({
      ...base,
      border: "none",
      boxShadow: "none",
      backgroundColor: "transparent",
      minHeight: 54,
      height: 54,
      padding: 0,
      cursor: "pointer",
      fontFamily: "inherit",
      fontSize: 16,
      letterSpacing: "-0.01em",
      color: "var(--ink)",
      "&:hover": { border: "none" },
    }),
    valueContainer: (base) => ({
      ...base,
      padding: 0,
    }),
    singleValue: (base) => ({
      ...base,
      color: "var(--ink)",
      margin: 0,
    }),
    placeholder: (base) => ({
      ...base,
      color: "var(--placeholder)",
      margin: 0,
    }),
    input: (base) => ({
      ...base,
      margin: 0,
      padding: 0,
      color: "var(--ink)",
    }),
    indicatorSeparator: () => ({ display: "none" }),
    dropdownIndicator: (base) => ({
      ...base,
      padding: "0 0 0 8px",
      color: "var(--chevron)",
      "&:hover": { color: "var(--chevron)" },
    }),
    // The control lives inside a grouped card with overflow: hidden, so the
    // menu is portalled to <body> (menuPortalTarget) and must sit above the
    // page but below dialogs (z-index 20+) and the busy overlay.
    menuPortal: (base) => ({
      ...base,
      zIndex: 15,
    }),
    menu: (base) => ({
      ...base,
      borderRadius: 14,
      overflow: "hidden",
      border: "none",
      boxShadow: "var(--card-shadow)",
      backgroundColor: "var(--surface)",
      zIndex: 10,
    }),
    menuList: (base) => ({
      ...base,
      padding: 0,
      borderRadius: 14,
      backgroundColor: "var(--surface)",
    }),
    option: (base, state) => ({
      ...base,
      padding: "14px 16px",
      fontSize: 16,
      color: "var(--ink)",
      backgroundColor: state.isSelected
        ? "var(--fill)"
        : state.isFocused
          ? "var(--row-hover)"
          : "var(--surface)",
      cursor: "pointer",
      "&:active": { backgroundColor: "var(--fill)" },
    }),
  };

  const refreshDropDownTheme = (base) => ({
    ...base,
    colors: {
      ...base.colors,
      primary: branding.sending_colors.sending_bg_secondary,
      primary25: "var(--row-hover)",
      primary50: "var(--fill)",
      neutral0: "var(--surface)",
      neutral80: "var(--ink)",
    },
    borderRadius: 14,
  });

  // Pre-refresh react-select styling, used with the legacy screens.
  const legacyDropDownStyle = {
    control: (base) => ({
      ...base,
      border: "none",
      backgroundColor: branding.sending_colors.sending_bg_tertiary,
      height: 54,
      padding: "0 6px",
    }),
    menuList: (base) => ({
      ...base,
      borderRadius: 12,
      backgroundColor: branding.sending_colors.sending_bg_primary,
    }),
    option: (base) => ({
      ...base,
      padding: 15,
      "&:hover": {
        backgroundColor: branding.sending_colors.sending_bg_secondary,
        color: branding.sending_colors.sending_bg_primary,
      },
    }),
  };

  const legacyDropDownTheme = (base) => ({
    ...base,
    colors: {
      primary: branding.sending_colors.sending_bg_tertiary,
    },
    borderRadius: 12,
  });

  const dropDownStyle = uiRefresh ? refreshDropDownStyle : legacyDropDownStyle;
  const dropDownTheme = uiRefresh ? refreshDropDownTheme : legacyDropDownTheme;

  // Apply per-field fallbacks so a partially-configured sms_customization
  // doesn't leave individual fields as undefined.
  const sms = branding.sms_customization || {};
  const defaultSms = defaultBranding.sms_customization;
  const missingField =
    !sms.off_net_first ||
    !sms.off_net_second ||
    !sms.off_net_third ||
    !sms.off_net_fourth ||
    !sms.on_net_first ||
    !sms.on_net_second ||
    !sms.on_net_third;
  if (missingField) {
    setBranding({
      ...branding,
      sms_customization: {
        off_net_first: sms.off_net_first || defaultSms.off_net_first,
        off_net_second: sms.off_net_second || defaultSms.off_net_second,
        off_net_third: sms.off_net_third || defaultSms.off_net_third,
        off_net_fourth: sms.off_net_fourth || defaultSms.off_net_fourth,
        on_net_first: sms.on_net_first || defaultSms.on_net_first,
        on_net_second: sms.on_net_second || defaultSms.on_net_second,
        on_net_third: sms.on_net_third || defaultSms.on_net_third,
      },
    });
  }

  return { branding, dropDownStyle, dropDownTheme, loaded, checked };
};
export default FiBranding;
